import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Package, Phone, User, Store, Clock, XCircle, CheckCircle2, Pencil, Search, Loader2 } from "lucide-react";
import { confirmDialog } from "../../utils/confirmDialog";
import OrderEditModal from "./OrderEditModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

/**
 * معالجة الطلبات — dedicated screen for handling pending customer orders.
 * Distinct from إدارة الطلبات / المعالجة chip: this is a card grid where
 * each invoice has 3 big actions: [إلغاء] [تعديل (blue)] [تأكيد]. The blue
 * edit button opens the products picker (OrderEditModal) with +/− and
 * delete. Requested Feb 27, 2026.
 */
const OrdersProcessingTab = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState({}); // id → true
  const [editing, setEditing] = useState(null); // order being edited

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/orders`);
      const list = (data || []).filter(
        (o) => o.status === "pending" && !o.is_direct_sale && !o.is_supervisor_sale
      );
      // Sort newest first
      list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setOrders(list);
    } catch {
      toast.error("فشل تحميل الطلبات");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return orders;
    return orders.filter((o) =>
      (o.user_name || "").toLowerCase().includes(s) ||
      (o.user_phone || "").toLowerCase().includes(s) ||
      (o.market_name || "").toLowerCase().includes(s) ||
      String(o.invoice_number || "").includes(s)
    );
  }, [orders, q]);

  const setBusyFor = (id, v) => setBusy((b) => ({ ...b, [id]: v }));

  const doCancel = async (order) => {
    const ok = await confirmDialog(
      `إلغاء فاتورة #${order.invoice_number} للزبون «${order.user_name || ""}»؟\n\n` +
        `سيتم:\n• إرجاع الكميات للمخزون\n• حذف الربح\n• خصم المديونية من الزبون\n• سحب نقاط المكافآت`,
    );
    if (!ok) return;
    setBusyFor(order.id, "cancel");
    try {
      await axios.patch(`${API}/admin/orders/${order.id}`, { status: "rejected" });
      toast.success("تم إلغاء الفاتورة");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الإلغاء");
    } finally { setBusyFor(order.id, false); }
  };

  const doConfirm = async (order) => {
    setBusyFor(order.id, "confirm");
    try {
      await axios.patch(`${API}/admin/orders/${order.id}`, { status: "confirmed" });
      toast.success(`✅ تم تأكيد فاتورة #${order.invoice_number}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل التأكيد");
    } finally { setBusyFor(order.id, false); }
  };

  return (
    <div className="space-y-4" data-testid="orders-processing-tab" dir="rtl">
      {/* Header */}
      <div className="bg-gradient-to-l from-[#0EA5E9] via-[#2563EB] to-[#7C3AED] text-white rounded-2xl p-4 shadow-md relative overflow-hidden">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Package className="w-6 h-6" />
            <div>
              <h3 className="font-black text-lg">معالجة الطلبات</h3>
              <p className="text-[11px] opacity-90">راجع، عدّل، وأكّد الفواتير المعلّقة</p>
            </div>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 text-sm font-black">
            <span dir="ltr">{filtered.length}</span> فاتورة معلّقة
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="ابحث برقم الفاتورة / اسم الزبون / الماركت..."
          data-testid="orders-processing-search"
          className="w-full pr-9 pl-4 py-2.5 rounded-full bg-white border border-[#E4EBE6] text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-16 text-[#5C6E64]">
          <Loader2 className="w-8 h-8 mx-auto animate-spin mb-2" />
          <p className="text-sm">جاري التحميل...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-[#E4EBE6]">
          <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-[#15803D] opacity-70" />
          <p className="text-[#5C6E64] font-medium">🎉 لا توجد فواتير معلّقة</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <AnimatePresence>
            {filtered.map((order) => (
              <motion.div
                key={order.id}
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -50 }}
                data-testid={`processing-card-${order.id}`}
                className="bg-white border-2 border-[#F5A623]/40 rounded-2xl p-3.5 shadow-sm hover:shadow-md transition-shadow"
              >
                {/* Header row */}
                <div className="flex items-start justify-between gap-2 mb-2 pb-2 border-b border-[#F3F6F4]">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <div className="bg-[#FEF3C7] text-[#B45309] rounded-full p-1.5 shrink-0">
                      <Clock className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-black text-[#1E3A5F] text-sm">
                        فاتورة <span dir="ltr">#{order.invoice_number}</span>
                      </p>
                      <p className="text-[10px] text-[#5C6E64]">
                        {new Date(order.created_at).toLocaleString("en-GB", { hour12: false })}
                      </p>
                    </div>
                  </div>
                  <div className="text-left shrink-0">
                    <p className="text-[10px] text-[#5C6E64]">الإجمالي</p>
                    <p className="font-black text-[#15803D] text-base" dir="ltr">
                      {fmt(order.total)} د.ع
                    </p>
                  </div>
                </div>

                {/* Customer info */}
                <div className="grid grid-cols-2 gap-2 mb-2 text-[11px]">
                  <div className="flex items-center gap-1.5 text-[#1E3A5F] truncate">
                    <User className="w-3.5 h-3.5 shrink-0" />
                    <span className="font-bold truncate">{order.user_name || "—"}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[#F5A623] truncate">
                    <Phone className="w-3.5 h-3.5 shrink-0" />
                    <span className="font-bold truncate" dir="ltr">{order.user_phone || "—"}</span>
                  </div>
                  {order.market_name && (
                    <div className="flex items-center gap-1.5 text-[#15803D] truncate col-span-2">
                      <Store className="w-3.5 h-3.5 shrink-0" />
                      <span className="font-bold truncate">{order.market_name}</span>
                    </div>
                  )}
                </div>

                {/* Items summary */}
                <div className="bg-[#F9FAFB] rounded-xl p-2 mb-2.5">
                  <p className="text-[11px] font-bold text-[#1E3A5F] mb-1">
                    📦 {(order.items || []).length} مادة · {(order.items || []).reduce((s, it) => s + (it.quantity || 0), 0)} كرتون
                  </p>
                  <div className="max-h-16 overflow-y-auto space-y-0.5">
                    {(order.items || []).slice(0, 3).map((it, i) => (
                      <p key={i} className="text-[10px] text-[#5C6E64] truncate">
                        • {it.name_ar || it.name} × <span dir="ltr">{it.quantity}</span>
                      </p>
                    ))}
                    {(order.items || []).length > 3 && (
                      <p className="text-[10px] text-[#3B82F6] font-bold">
                        + {(order.items || []).length - 3} مادة أخرى...
                      </p>
                    )}
                  </div>
                </div>

                {/* 3 action buttons */}
                <div className="grid grid-cols-3 gap-1.5">
                  <button
                    onClick={() => doCancel(order)}
                    disabled={!!busy[order.id]}
                    data-testid={`processing-cancel-${order.id}`}
                    className="bg-[#E85D45] hover:bg-[#C24A35] disabled:opacity-50 text-white text-xs font-black py-2 rounded-xl flex items-center justify-center gap-1 transition-colors active:scale-95"
                  >
                    <XCircle className="w-3.5 h-3.5" />
                    {busy[order.id] === "cancel" ? "..." : "إلغاء"}
                  </button>
                  <button
                    onClick={() => setEditing(order)}
                    disabled={!!busy[order.id]}
                    data-testid={`processing-edit-${order.id}`}
                    className="bg-[#3B82F6] hover:bg-[#2563EB] disabled:opacity-50 text-white text-xs font-black py-2 rounded-xl flex items-center justify-center gap-1 transition-colors active:scale-95 shadow-md ring-2 ring-[#3B82F6]/30"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    تعديل
                  </button>
                  <button
                    onClick={() => doConfirm(order)}
                    disabled={!!busy[order.id]}
                    data-testid={`processing-confirm-${order.id}`}
                    className="bg-[#15803D] hover:bg-[#126B32] disabled:opacity-50 text-white text-xs font-black py-2 rounded-xl flex items-center justify-center gap-1 transition-colors active:scale-95"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {busy[order.id] === "confirm" ? "..." : "تأكيد"}
                  </button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {editing && (
        <OrderEditModal
          order={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
};

export default OrdersProcessingTab;
